import { DialogTrigger } from "@egaranti/components";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  FileLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Label,
  ScrollArea,
  useToast,
} from "@egaranti/components";
import { zodResolver } from "@hookform/resolvers/zod";

import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Editor from "react-simple-wysiwyg";

import documentsService from "@/services/documentsService";

import * as z from "zod";

const CreateDefaultDocumentModal = ({ children, setRelatedProductCount }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isExist, setIsExist] = useState(false);
  const [initialLogo, setInitialLogo] = useState(null);
  const [initialSignature, setInitialSignature] = useState(null);

  const form = useForm({
    resolver: zodResolver(
      z.object({
        name: z.string(),
        address: z.string(),
        phone: z.string(),
        fax: z.string(),
        warrantyConditions: z.string().optional(),
        logo: z.any().refine(
          (file) => {
            if (initialLogo) return true;
            return file instanceof File;
          },
          {
            message: t("common.validateMsg.requiredField"),
          },
        ),
        signature: z.any().refine(
          (file) => {
            if (initialLogo) return true;
            return file instanceof File;
          },
          {
            message: t("common.validateMsg.requiredField"),
          },
        ),
      }),
    ),
    defaultValues: {
      name: "",
      address: "",
      phone: "",
      fax: "",
      logo: "",
      signature: "",
      warrantyConditions: "",
    },
  });

  const getWarrantyDocuments = () => {
    documentsService.getDefaultWarrantyDocuments().then((res) => {
      form.reset(res);
      setInitialLogo(res.logo);
      setInitialSignature(res.signature);
      setRelatedProductCount(res.relatedProductCount);
      setIsExist(res.id);
    });
  };

  const imageInputRef = useRef(null);

  const toBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  const toDataURL = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleInsertImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await toDataURL(file);
    const imgTag = `<img src="${dataUrl}" alt="${file.name}" style="max-width: 100%;" />`;
    const currentValue = form.getValues("warrantyConditions") || "";
    form.setValue("warrantyConditions", currentValue + imgTag);
    e.target.value = "";
  };

  const onSubmit = async (data) => {
    const payload = {
      id: isExist,
      ...data,
    };

    if (data.logo && data.logo instanceof File) {
      payload.logo = {
        name: data.logo.name,
        content: await toBase64(data.logo),
        mimeType: data.logo.type,
      };
    } else {
      payload.logo = null;
    }
    if (data.signature && data.signature instanceof File) {
      payload.signature = {
        name: data.signature.name,
        content: await toBase64(data.signature),
        mimeType: data.signature.type,
      };
    } else {
      payload.signature = null;
    }

    if (isExist) {
      documentsService.updateDefaultWarrantyDocument(payload).then(() => {
        toast({
          title: t("documentsPage.defaultDocumentCreated"),
          variant: "success",
        });
      });
    } else {
      documentsService.createDefaultWarrantyDocument(payload).then(() => {
        toast({
          title: t("documentsPage.defaultDocumentCreated"),
          variant: "success",
        });
      });
    }
  };

  useEffect(() => {
    getWarrantyDocuments();
  }, []);

  return (
    <Dialog
      onOpenChange={(isOpen) => {
        getWarrantyDocuments();
      }}
    >
      <DialogTrigger>{children}</DialogTrigger>
      {/*
        EPS-5120 (QA turu) — pencere telefonda ekrandan genisti: baslik, aciklama
        ve form alanlarinin sagi kesiliyordu. Paylasilan DialogContent `w-full
        max-w-lg` ile geliyor; buradaki `max-w-fit` o siniri kaldirdigi icin kutu
        iceriginin genisligine gore buyuyup ekrani asiyordu.

        Telefonda ekran genisligi eksi kenar boslugu ile sinirlaniyor, sm ve
        uzerinde masaustundeki "icerik kadar" davranisi aynen korunuyor.
      */}
      <DialogContent className="max-w-[calc(100vw-2rem)] bg-[#F9FAFB] pb-0 sm:max-w-fit">
        <DialogTitle>Varsayılan Garanti Belgesi Oluştur</DialogTitle>
        {/*
          Pencereyi daraltmak tek basina yetmiyordu: DialogContent bir `grid`
          ve grid cocuklarinin ortulu `min-width: auto` tabani ScrollArea'nin
          icerikten daha dar olmasini engelliyor. Sonucta kutu ekrana sigiyor
          ama basligin ve alanlarin sagi kutunun disinda kalip kirpiliyordu
          (`overflow-hidden`). `min-w-0` o tabani kaldirir, `w-full` da icerigi
          kutunun genisligine oturtur.
        */}
        {/*
          Radix ScrollArea, Viewport'un icine `display: table` + `min-width: 100%`
          bir sarmalayici koyuyor. `table` kutusu icerige gore buyudugu icin
          (olculdu: 390 px ekranda 502 px) disaridaki `w-full`/`min-w-0`
          kurallarinin hicbiri ise yaramiyor, icerik pencerenin disina tasip
          kirpiliyordu. O sarmalayici blok yapilinca ebeveyn genisligine oturuyor.
        */}
        <ScrollArea className="h-[550px] w-full min-w-0 border-b [&>div>div]:!block [&>div>div]:!w-full">
          <div className="flex w-full min-w-0 flex-col items-center gap-4 px-4 py-10 sm:px-0">
            <svg
              width="72"
              height="72"
              viewBox="0 0 72 72"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="4" y="4" width="64" height="64" rx="32" fill="#CCDBFA" />
              <rect
                x="4"
                y="4"
                width="64"
                height="64"
                rx="32"
                stroke="#AAC2F7"
                stroke-width="8"
              />
              <path
                d="M46.6668 33.9998V29.0665C46.6668 26.8263 46.6668 25.7062 46.2309 24.8505C45.8474 24.0979 45.2354 23.486 44.4828 23.1025C43.6271 22.6665 42.507 22.6665 40.2668 22.6665H31.7335C29.4933 22.6665 28.3732 22.6665 27.5175 23.1025C26.7649 23.486 26.153 24.0979 25.7695 24.8505C25.3335 25.7062 25.3335 26.8263 25.3335 29.0665V42.9332C25.3335 45.1734 25.3335 46.2935 25.7695 47.1491C26.153 47.9018 26.7649 48.5137 27.5175 48.8972C28.3732 49.3332 29.4933 49.3332 31.7335 49.3332H36.0002M38.6668 34.6665H30.6668M33.3335 39.9998H30.6668M41.3335 29.3332H30.6668M44.0002 47.9998V39.9998M40.0002 43.9998H48.0002"
                stroke="#0049E6"
                stroke-width="2.5"
                strokeLinecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <h2 className="text-lg font-semibold text-[#111729]">
              Varsayılan Garanti Belgesi Oluştur
            </h2>
            {/* <Button
              variant="secondaryGray"
              size="full"
              onClick={() => documentsService.previewDefaultWarrantyDocument()}
            >
              Önizle
            </Button> */}
            <p className="text-center font-medium text-[#677389]">
              Garanti belgesinde bulunması gereken alanları doldurunuz.
            </p>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="mt-5 w-full space-y-5"
              >
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <Label>Firma Adı</Label>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <Label>Adres</Label>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <Label>Telefon</Label>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="fax"
                  render={({ field }) => (
                    <FormItem>
                      <Label>Faks</Label>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {initialLogo && form.watch("logo") ? (
                  <div className="flex flex-col gap-2">
                    <Label>Logo</Label>
                    <FileLink
                      onDeleted={() => {
                        form.setValue("logo", null);
                        setInitialLogo(null);
                      }}
                      url={
                        typeof form.watch("logo") === "string"
                          ? form.watch("logo")
                          : ""
                      }
                    />
                  </div>
                ) : (
                  <FormField
                    control={form.control}
                    name="logo"
                    render={({ field }) => (
                      <FormItem>
                        <Label>Logo</Label>
                        <FormControl>
                          <Input
                            onChange={(e) =>
                              field.onChange(
                                e.target.files ? e.target.files[0] : null,
                              )
                            }
                            type="file"
                            accept=".jpg, .jpeg, .png"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                {initialSignature && form.watch("signature") ? (
                  <div className="flex flex-col gap-2">
                    <Label>İmza/Kaşe</Label>
                    <FileLink
                      onDeleted={() => {
                        form.setValue("signature", null);
                        setInitialSignature(null);
                      }}
                      url={
                        typeof form.watch("signature") === "string"
                          ? form.watch("signature")
                          : ""
                      }
                    />
                  </div>
                ) : (
                  <FormField
                    control={form.control}
                    name="signature"
                    render={({ field }) => (
                      <FormItem>
                        <Label>İmza/Kaşe</Label>
                        <FormControl>
                          <Input
                            type="file"
                            accept=".jpg, .jpeg, .png"
                            onChange={(e) => {
                              console.log(e.target.files[0]);
                              field.onChange(
                                e.target.files ? e.target.files[0] : null,
                              );
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                <FormField
                  control={form.control}
                  name="warrantyConditions"
                  render={({ field }) => (
                    <FormItem>
                      <Label>Garanti Şartları</Label>
                      <FormControl>
                        <div>
                          <Editor {...field} />
                          <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleInsertImage}
                          />
                          <Button
                            type="button"
                            variant="secondaryGray"
                            size="sm"
                            className="mt-2"
                            onClick={() => imageInputRef.current?.click()}
                          >
                            Resim Ekle
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </div>
        </ScrollArea>
        <div className="flex w-full justify-center gap-4 pb-5">
          <DialogClose asChild>
            <Button variant="secondaryGray" size="full">
              Vazgeç
            </Button>
          </DialogClose>
          <Button size="full" onClick={form.handleSubmit(onSubmit)}>
            Kaydet
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDefaultDocumentModal;
